import { useProfile } from "@/context/ProfileContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { markActive } from "@/lib/seatTime";
import { stopSpeaking } from "@/lib/buddy";

const SKIP = new Set(["code", "pre"]);

export function rehypeWordSpans() {
  return (tree) => {
    let i = 0;
    const walk = (node) => {
      if (!node.children || SKIP.has(node.tagName)) return;
      node.children = node.children.flatMap((c) => {
        if (c.type !== "text") { walk(c); return [c]; }
        return c.value.split(/(\s+)/).filter(Boolean).map((part) => (/^\s+$/.test(part) ? { type: "text", value: part }
          : { type: "element", tagName: "span", properties: { className: ["rw"], dataWi: i++ }, children: [{ type: "text", value: part }] }));
      });
    };
    walk(tree);
  };
}

const norm = (w) => w.toLowerCase().replace(/[^a-z0-9]/g, "");

function align(domWords, ttsWords) {
  let p = 0;
  return ttsWords.map((t) => {
    const n = norm(t.w);
    for (let j = p; j < Math.min(p + 8, domWords.length); j++) {
      if (n && norm(domWords[j]) === n) { p = j + 1; return j; }
    }
    return Math.min(p, domWords.length - 1);
  });
}

export const SPEEDS = [[0.75, "Slow"], [1, "Normal"], [1.25, "Fast"]];
export const speedFor = (profile) => profile?.read_speed || (["K", "1", "2"].includes(profile?.grade) ? 0.75 : 1);

export function useReadAlong() {
  const { profile } = useProfile();
  const speed = speedFor(profile);
  const speedRef = useRef(speed);
  const [state, setState] = useState("idle");
  const [owner, setOwner] = useState(null);
  const s = useRef({});

  const paint = useCallback((cur) => {
    const { spans, last = -1 } = s.current;
    if (!spans || cur === last) return;
    if (last >= 0 && spans[last]) spans[last].classList.remove("rw-on");
    if (cur > last) for (let k = Math.max(0, last); k < cur; k++) spans[k]?.classList.add("rw-read");
    else for (let k = cur; k <= last; k++) spans[k]?.classList.remove("rw-read");
    if (cur >= 0 && spans[cur]) {
      spans[cur].classList.add("rw-on");
      const r = spans[cur].getBoundingClientRect();
      if (r.top < 90 || r.bottom > window.innerHeight - 40) spans[cur].scrollIntoView({ block: "center", behavior: "smooth" });
    }
    s.current.last = cur;
  }, []);

  const clear = useCallback(() => {
    const { spans, audio, raf } = s.current;
    cancelAnimationFrame(raf);
    audio?.pause();
    spans?.forEach((el) => el.classList.remove("rw-on", "rw-read"));
    if (!s.current.root?.dataset.keepActive) s.current.root?.classList.remove("rw-active");
    s.current = {};
    setState("idle"); setOwner(null);
  }, []);

  const tick = useCallback(() => {
    const { audio, words, map } = s.current;
    if (!audio) return;
    const t = audio.currentTime;
    let lo = 0, hi = words.length - 1, idx = -1;
    while (lo <= hi) { const m = (lo + hi) >> 1; if (words[m].s <= t + 0.05) { idx = m; lo = m + 1; } else hi = m - 1; }
    paint(idx >= 0 ? map[idx] : -1);
    if (!audio.paused) s.current.raf = requestAnimationFrame(tick);
  }, [paint]);

  const seekDom = useCallback((domIdx) => {
    const { audio, words, map } = s.current;
    if (!audio) return;
    const k = map.findIndex((m) => m >= domIdx);
    audio.currentTime = k >= 0 ? words[k].s : 0;
    if (audio.paused) { audio.play(); setState("playing"); s.current.raf = requestAnimationFrame(tick); } else tick();
  }, [tick]);

  const play = useCallback(async (text, voice, root, id, fromDom = 0) => {
    if (s.current.id === id && s.current.audio) {
      if (!s.current.audio.paused) { s.current.audio.pause(); cancelAnimationFrame(s.current.raf); setState("paused"); return; }
      return seekDom(fromDom);
    }
    clear(); stopSpeaking();
    setState("loading"); setOwner(id);
    try {
      const [a, w] = await Promise.all([http.post("/tts", { text, voice }, { responseType: "blob" }), http.post("/tts/words", { text, voice })]);
      const spans = Array.from(root.querySelectorAll("[data-wi]"));
      const audio = new Audio(URL.createObjectURL(a.data));
      audio.playbackRate = speedRef.current;
      s.current = { id, audio, root, spans, words: w.data.words, map: align(spans.map((el) => el.textContent), w.data.words), last: -1 };
      root.classList.add("rw-active");
      audio.ontimeupdate = markActive;
      audio.onended = () => clear();
      audio.onplay = () => { s.current.raf = requestAnimationFrame(tick); };
      if (fromDom > 0) seekDom(fromDom); else { await audio.play(); setState("playing"); }
    } catch { clear(); throw new Error("voice unavailable"); }
  }, [clear, seekDom, tick]);

  const toggle = useCallback(() => {
    const { audio } = s.current;
    if (!audio) return;
    if (audio.paused) { audio.play(); setState("playing"); } else { audio.pause(); cancelAnimationFrame(s.current.raf); setState("paused"); }
  }, []);

  const pause = useCallback(() => {
    const { audio } = s.current;
    if (audio && !audio.paused) { audio.pause(); cancelAnimationFrame(s.current.raf); setState("paused"); }
  }, []);

  useEffect(() => { speedRef.current = speed; if (s.current.audio) s.current.audio.playbackRate = speed; }, [speed]);
  useEffect(() => clear, [clear]);
  return { state, owner, play, toggle, pause, stop: clear };
}

export const wordIndexOf = (e) => {
  const el = e.target.closest?.("[data-wi]");
  return el ? Number(el.dataset.wi) : null;
};
